import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  UniversalFilterCompanyNewFinStatusStatus,
  UniversalFilterCompanyNewFinStatusValue,
  UniversalFilterCompanyNewFinStatusModes,
  UniversalFilterCompanyNewFinStatusFilterState,
  UniversalFilterCompanyNewFinStatusOnChange
} from '../../UniversalFilterCompanyNewFinStatus.types';

interface UniversalFilterCompanyNewFinStatusFilterStateOptions {
  onChange: UniversalFilterCompanyNewFinStatusOnChange;
}

function useUniversalFilterCompanyNewFinStatusFilterState({
  onChange
}: UniversalFilterCompanyNewFinStatusFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterCompanyNewFinStatusFilterState) => void
  >(
    (filterState) => {
      const statuses = filterState?.statuses;

      if (
        filterState?.mode === UniversalFilterCompanyNewFinStatusModes.IS_ANY_OF
      ) {
        onChange(isEmpty(statuses) ? undefined : { in: statuses });
        return;
      }

      if (
        filterState?.mode === UniversalFilterCompanyNewFinStatusModes.IS_NONE_OF
      ) {
        onChange(isEmpty(statuses) ? undefined : { notIn: statuses });
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterCompanyNewFinStatusValue
    ) => UniversalFilterCompanyNewFinStatusFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (!isEmpty(filterValue.in)) {
      return {
        mode: UniversalFilterCompanyNewFinStatusModes.IS_ANY_OF,
        statuses: filterValue.in as UniversalFilterCompanyNewFinStatusStatus[]
      };
    }

    if (!isEmpty(filterValue.notIn)) {
      return {
        mode: UniversalFilterCompanyNewFinStatusModes.IS_NONE_OF,
        statuses:
          filterValue.notIn as UniversalFilterCompanyNewFinStatusStatus[]
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterCompanyNewFinStatusFilterState;
